import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import { FORM_TYPES, FormTypeSwitch } from '../manageComponents/FormTypeSwitch';
import { NavigationHeader } from '../../../components/NavigationHeader';
import Heading from '../../../generic-components/Heading';
import { DynamicForm } from '../../../components/form/dynamicForm';
import { useGetSupplierById } from '../hooks/useGetSupplierById';
import { useCreateSupplier } from '../hooks/useCreateSupplier';
import {
  generateUpdateSupplierPayload,
  getSupplierDataById,
  handleBackCallbackByContext,
  handleDeleteCallbackByContext,
  handleSubmitCallbackByContext,
} from '../helpers';
import { VIEWS_MAP } from '../../../router/constants';
import { useDeleteSupplier } from '../hooks/useDeleteSupplier';
import Button from '../../../generic-components/Button';
import { FORM_FIELDS } from '../../../components/form/dynamicForm/consts';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { ReactComponent as LeftArrow } from '../../../assets/svg/left-arrow-title.svg';
import { DeleteConfirmation } from '../manageComponents/DeleteConfirmation';
import { DeleteVendorError } from '../manageComponents/DeleteVendorError';

export const ManageSupplierV2 = () => {
  const [deleteErrorMsg, setDeleteErrorMsg] = useState<string>('');
  const [activeFormType, setActiveFormType] = useState<string>(FORM_TYPES.BUSINESS);
  const [isDeleteConfirm, setIsDeleteConfirm] = useState<boolean>(false);
  const { state: navState } = useLocation();
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  /** Get Supplier By Id if available */
  const { data: supplierData, isLoading, refetch } = useGetSupplierById({ supplierExternalId: supplierId || '' });
  /** Create / Update supplier */
  const { mutateAsync, isLoading: isLoadingUpdate } = useCreateSupplier();
  /** Delete supplier */
  const { mutateAsync: asyncDeleteSupplier, isLoading: isLoadingDelete } = useDeleteSupplier((msg: string) =>
    setDeleteErrorMsg(msg));

  const isFormLoadingState = useMemo(() => isLoadingDelete || isLoadingUpdate, [isLoadingDelete, isLoadingUpdate]);

  const handleSubmit = async (formData: any) => {
    trackMixPanel('Act on Add Payee Screen', {
      Action: supplierId ? 'Save' : 'Continue',
      'Payee Type': activeFormType,
    });

    const res = await mutateAsync(generateUpdateSupplierPayload(formData, supplierId || '', activeFormType));
    const data = await getSupplierDataById(res.supplierId);

    handleSubmitCallbackByContext({
      context: navState.formType,
      supplierData: data,
      navigate,
      id: supplierId,
      transferAmount: navState?.transferAmount,
    });
  };

  const handleBack = () => {
    trackMixPanel('Act on Add Payee Screen', { Action: 'Back' });
    if (navState?.customBackUrl) {
      navigate(navState?.customBackUrl);
    } else {
      handleBackCallbackByContext({
        context: navState.formType,
        supplierData,
        navigate,
        id: supplierId,
        isNativeBack: navState.isNativeBack,
      });
    }
  };

  const handleDelete = async () => {
    setIsDeleteConfirm(false);
    trackMixPanel('Act on Add Payee Screen', { Action: 'Delete' });
    await asyncDeleteSupplier({ supplierExternalId: supplierId || '' });
    handleDeleteCallbackByContext({ context: navState.formType });
    navigate(`/${VIEWS_MAP.SUPPLIER.BASE}/${VIEWS_MAP.SUPPLIER.PICKER}`, {
      state: {
        upperMessage: `<b> ${supplierData?.name} </b> deleted`,
        referrer: navState.formType,
      },
    });
  };

  useEffect(() => {
    if (supplierData?.supplierEntityType) {
      setActiveFormType(supplierData.supplierEntityType);
    }
  }, [supplierData]);

  useEffect(() => {
    if (supplierId) {
      refetch();
    }
  }, [supplierId]);

  useEffect(() => {
    trackMixPanel('View Add Payee Screen');
  }, []);

  const handleFormTypeChange = (formType: string) => {
    const elLabelBusinessName = document.getElementById(`elLabel-${FORM_FIELDS.BUSINESS_NAME}`);
    if (!elLabelBusinessName) return;
    elLabelBusinessName.textContent =
      formType === FORM_TYPES.BUSINESS
        ? t('RECIPIENT_MANAGER_NAME_LABELS.BUSINESS')
        : t('RECIPIENT_MANAGER_NAME_LABELS.INDIVIDUAL');
    setActiveFormType(formType);
  };

  /** navState values */
  /** pageTitle: NavigationHeader page title  */
  /** formHeader: Heading title  */
  /** preFilledName: A pre filled supplier name  */
  /** formType: Form model type to render  */
  /** onExitDestination: The route value to navigate upon user exit the wizard  */
  /** isNativeBack: Redirect back to supplier picker without effects  */
  /** customBackUrl: Redirect back to supplier picker without effects  */
  /** isUpdateOnlyMode: Show only update option without delete  */
  return (
    <div className='absolute inset-0 z-[50] bg-bw00 h-screen w-screen flex flex-col items-center fadeIn overflow-hidden'>
      {isDeleteConfirm && (
        <DeleteConfirmation
          closePopUp={() => setIsDeleteConfirm(false)}
          handleDelete={handleDelete}
          supplierName={supplierData?.name || ''}
        />
      )}
      {deleteErrorMsg && <DeleteVendorError onClose={() => setDeleteErrorMsg('')} errMsg={deleteErrorMsg} />}
      <NavigationHeader
        onCloseAction={() => navigate(navState?.onExitDestination || `/${VIEWS_MAP.DASHBOARD}`)}
        pageTitle={navState?.pageTitle}
      />
      <div className='flex items-center justify-center relative mt-[64px] mb-[24px]'>
        {(supplierId || navState?.isNativeBack) && (
          <Button
            onClick={handleBack}
            className='absolute left-0 translate-x-[-200px]'
            id='elBackButton'
            childElement={
              <div className='flex items-center gap-[6px]'>
                <LeftArrow className='w-[16px]' />
                <Heading title='Back' className='font-metricR text-[16px] text-bw08 underline' />
              </div>
            }
          />
        )}
        <Heading title={navState?.formHeader} className='font-metricRMedium text-[28px] text-center text-bw08' />
      </div>
      {!navState?.isUpdateOnlyMode && (
        <FormTypeSwitch activeTabId={activeFormType} onTabSelection={(type: string) => handleFormTypeChange(type)} />
      )}
      <div className='w-full flex justify-center grow overflow-auto'>
        <div className='w-[375px]'>
          {isLoading ? (
            <div className='flex flex-col'>
              <Skeleton className='mb-[24px]' height='50px' count={5} />
            </div>
          ) : (
            <DynamicForm
              preFilledValue={{ inputId: FORM_FIELDS.BUSINESS_NAME, value: navState?.preFilledName }}
              isLoading={isFormLoadingState}
              triggerDelete={() => setIsDeleteConfirm(true)}
              isUpdateOnlyMode={navState?.isUpdateOnlyMode}
              isEditMode={navState?.isUpdateOnlyMode ? false : !!supplierId}
              preFillData={supplierData}
              triggerFormSubmit={handleSubmit}
              formType={navState?.formType}
            />
          )}
        </div>
      </div>
    </div>
  );
};
